import {useAgencyPaymentMethodsQuery} from "@/api/agencyApi";
import CampaignIcon from "@/assets/colorable/campaign.svg?react";
import CancelPlanIcon from "@/assets/colorable/cancel_plan.svg?react";
import CircularArrowsIcon from "@/assets/colorable/circular_arrows.svg?react";
import InfinityIcon from "@/assets/colorable/infinity.svg?react";
import InstagramIcon from "@/assets/colorable/instagram.svg?react";
import MailPlusIcon from "@/assets/colorable/mail_plus.svg?react";
import PlusIcon from "@/assets/colorable/plus.svg?react";
import RightArrowIcon from "@/assets/colorable/right_arrow.svg?react";
import SourceContentIcon from "@/assets/colorable/source_content.svg?react";
import TiktokIcon from "@/assets/colorable/tiktok.svg?react";
import NoPackageCircleIcon from "@/assets/dark_gradient/no_package_circle.svg?react";
import Button from "@/components/Common/Button";
import InputField from "@/components/Common/forms/InputField";
import {Tooltip} from "@/components/Common/Tooltip";
import SkeletonLoader from "@/components/onboarding/SkeletonLoader";
import LockedFeaturesList from "@/components/plans/LockedFeaturesList";
import UnlockedFeaturesList from "@/components/plans/UnlockedFeaturesList";
import PaymentFailedBanner from "@/components/Settings/PaymentFailedBanner";
import CountrySelect from "@/components/ui/CountrySelect";
import useCountries from "@/hooks/useCountries";
import {getPackageIcon, packages} from "@/hooks/usePackages";
import {usePayment} from "@/hooks/usePayment";
import {usePaymentFailedInvoices} from "@/hooks/usePaymentFailedInvoices";
import {rememberStripeSetupReturn} from "@/hooks/useStripeSetupReturn";
import clsxm from "@/lib/clsxm";
import AddPaymentMethodForm from "@/pages/profile/AddPaymentMethodForm";
import CardComponent from "@/pages/profile/CardComponent";
import {AbilityContext, Can} from "@/permissions";
import {useSettingsAgencyStore} from "@/store/agency";
import {useSettingsCompanyStore} from "@/store/company";
import {FULL_DATE_FORMAT, INFINITY_THRESHOLD} from "@/store/constants";
import {settingsModalAtom} from "@/store/jotaiAtoms";
import type {TSettingsScope} from "@/store/types";
import useUserStore from "@/store/user";
import {getAdminAssignedTrialDaysLeft, isActiveAdminAssignedTrial} from "@/utils/planUtils";
import {useNavigate} from "@tanstack/react-router";
import dayjs from "dayjs";
import {useSetAtom} from "jotai";
import {useContext, useState, type FC} from "react";
import {useTranslation} from "react-i18next";

const SKELETON_FEATURE_COUNT = 6;

const BillingPane: FC<{scope: TSettingsScope}> = ({scope}) => {
  const {t} = useTranslation(["common", "settings", "multi_account"]);
  const navigate = useNavigate();
  const ability = useContext(AbilityContext);
  const setSettingsModal = useSetAtom(settingsModalAtom);
  const {getCountryCodeByEnLabel} = useCountries();
  const selfAgencies = useUserStore(state => state.user.agencies);
  const selfSubBrands = useUserStore(state => state.user.sub_brands);
  const scopeAgency = useSettingsAgencyStore(state => state.agency);
  const company = useSettingsCompanyStore(state => state.company);
  const [isAddingPaymentMethod, setIsAddingPaymentMethod] = useState(false);

  const isAgencyScope = scope.type === "agency";
  const scopeHolder = isAgencyScope ? scopeAgency : company;

  const scopeSubscription = scopeHolder?.package_subscription;
  const isSubscriptionExpired =
    !!scopeSubscription?.valid_until && dayjs(scopeSubscription.valid_until).isBefore(dayjs());
  const subscription = isSubscriptionExpired ? null : scopeSubscription;
  const pkg = packages[subscription?.name!];
  const isSubscriptionActive = subscription?.status === "active" || subscription?.status === "trialing";
  const isSubbrand = !isAgencyScope && company.company_access_level === "SUB_BRAND";
  const packageIncludedBuyouts = subscription?.buyout ?? 0;

  const bundles = scopeHolder?.buyouts ?? [];
  const campaignsLeft =
    subscription?.number_of_campaigns_left ?? scopeHolder?.future_subscription?.number_of_campaigns_left ?? 100_000;
  const agencyId = isAgencyScope ? scopeAgency.id : company.agency?.id;
  const isViewingSelf = isAgencyScope
    ? !!selfAgencies?.some(member => member.agency?.id === scope.id)
    : !!selfSubBrands?.some(subBrand => subBrand.company.id === scope.id);

  const billingAddress = {
    city: (isAgencyScope ? scopeAgency.city : company.address_city) ?? "",
    country: isAgencyScope ? scopeAgency.country : company.country,
    postal: (isAgencyScope ? scopeAgency.postal_code : company.address_postal) ?? "",
    street: (isAgencyScope ? scopeAgency.address : company.address_street) ?? "",
  };
  const remainingBuyouts =
    packageIncludedBuyouts +
    bundles.reduce((total, bundle) => total + bundle.buyout!, 0) -
    (subscription?.buyout_used ?? 0) -
    bundles.reduce((total, bundle) => total + bundle.buyout_used, 0);

  const {data: paymentMethods} = useAgencyPaymentMethodsQuery({agencyId});
  const {getPaymentFailedInvoices} = usePaymentFailedInvoices();
  const paymentFailedInvoices = getPaymentFailedInvoices({scope});
  const canAddPaymentMethod = !isAgencyScope && isViewingSelf;
  const {setDefaultLoading, setDefaultPaymentMethod} = usePayment({agencyId, companyId: company.id});

  const openPlans = () =>
    ability.can("read", "AdminMenu") ? setSettingsModal({scope, section: "admin_billing"}) : navigate({to: "/plans"});

  if (scopeHolder?.id !== scope.id) {
    return (
      <div className="flex flex-col gap-8">
        <span className="text-neutral-black text-lg leading-none font-bold">{t("settings:billing")}</span>
        <div className="flex flex-col gap-1">
          <SkeletonLoader className="h-4 max-w-20" />
          <div className="border-neutral-grey-light-dark flex h-14 items-center justify-between gap-2 rounded-lg border px-3">
            <SkeletonLoader className="h-4 max-w-40" />
            <SkeletonLoader className="h-10 w-32 shrink-0 rounded-lg" />
          </div>
        </div>
        <div className="flex flex-col gap-1">
          <SkeletonLoader className="h-4 max-w-32" />
          <div className="border-neutral-grey-light-dark flex flex-col rounded-lg border">
            <div className="flex h-10 items-center px-3">
              <SkeletonLoader className="h-4 max-w-16" />
            </div>
            <div className="bg-neutral-grey-light-dark h-px w-full" />
            <div className="flex h-9 items-center px-3">
              <SkeletonLoader className="h-3 max-w-28" />
            </div>
          </div>
        </div>
        <div className="flex flex-col gap-1">
          <SkeletonLoader className="h-4 max-w-32" />
          <div className="flex flex-wrap gap-x-6 gap-y-2">
            {Array.from({length: SKELETON_FEATURE_COUNT}).map((_, index) => (
              <SkeletonLoader className="h-4 max-w-36" key={index} />
            ))}
          </div>
        </div>
        <div className="flex flex-col gap-2">
          <SkeletonLoader className="h-4 max-w-28" />
          <SkeletonLoader className="h-11 rounded-lg" />
          <div className="flex flex-row gap-2">
            <SkeletonLoader className="h-11 rounded-lg" />
            <SkeletonLoader className="h-11 rounded-lg" />
          </div>
          <SkeletonLoader className="h-11 rounded-lg" />
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-8">
      <span className="text-neutral-black text-lg leading-none font-bold">{t("settings:billing")}</span>
      {paymentFailedInvoices.length > 0 && <PaymentFailedBanner invoices={paymentFailedInvoices} scope={scope} />}
      <Can I="update" a="CompanyPlan" passThrough>
        {allowed => (
          <div className="flex flex-col gap-1">
            <span className="text-neutral-black text-sm font-medium">{t("common:package")}</span>
            {pkg ? (
              <div className="flex flex-col gap-2" id="package">
                <div className="border-neutral-grey-light-dark bg-neutral-grey-lightest flex w-full flex-wrap items-center justify-between gap-x-2 gap-y-3 rounded-lg border px-3 py-2">
                  <div className="flex items-center gap-2">
                    {getPackageIcon({className: "size-6 font-normal", packageType: pkg.type})}
                    <span className="text-neutral-black text-sm">
                      {t(pkg.translationKey as "packageAdvanced")} {t("common:package")}
                    </span>
                    <div className="flex items-center gap-1">
                      {subscription?.instagram_campaigns && (
                        <InstagramIcon className="text-neutral-black size-4 shrink-0" />
                      )}
                      {subscription?.tiktok_campaigns && <TiktokIcon className="text-neutral-black size-4 shrink-0" />}
                    </div>
                  </div>
                  {allowed && (
                    <div className="flex flex-wrap items-center gap-2">
                      {isSubscriptionActive &&
                        (subscription.cancellation_date ? (
                          <div className="border-danger-raspberry bg-danger-light-red flex min-h-6 items-center rounded-full border px-2 py-px">
                            <span className="text-neutral-black text-2xs leading-3 font-semibold uppercase">
                              {t("common:package_cancelled_label", {
                                date: dayjs(subscription.valid_until).format(FULL_DATE_FORMAT),
                              })}
                            </span>
                          </div>
                        ) : (
                          <Button
                            buttonStyle="White"
                            className="h-10 gap-2 px-4 text-sm"
                            onClick={() =>
                              setSettingsModal(state => state && {...state, subview: {type: "cancel_plan"}})
                            }>
                            <CancelPlanIcon className="text-primary-dark-solid" />
                            {t("common:cancelPlan")}
                          </Button>
                        ))}
                      <Button buttonStyle="Blue" className="h-10 gap-2 px-4 text-sm" onClick={openPlans}>
                        <CircularArrowsIcon className="text-white" />
                        {t("common:changeYourPlan")}
                      </Button>
                    </div>
                  )}
                </div>
                {isActiveAdminAssignedTrial(subscription) ? (
                  <div className="flex flex-col gap-1">
                    <span className="text-neutral-black text-xs font-semibold">
                      {t("common:you_are_on_a_free_trial")}
                    </span>
                    <span className="text-neutral-grey-dark text-xs font-medium">
                      {t("common:free_trial_ends_on", {
                        count: getAdminAssignedTrialDaysLeft(subscription),
                        date: dayjs(subscription?.valid_until).format(FULL_DATE_FORMAT),
                      })}{" "}
                      {t("common:contact_sales_to_continue_after_trial")}
                    </span>
                  </div>
                ) : (
                  !!subscription?.valid_until &&
                  !subscription.cancellation_date && (
                    <span className="text-neutral-grey-dark text-xs font-medium">
                      {t("common:nextPaymentDate")}: {dayjs(subscription.valid_until).format(FULL_DATE_FORMAT)}
                    </span>
                  )
                )}
              </div>
            ) : (
              <div
                id="package"
                className={clsxm(
                  "border-neutral-grey-light-dark bg-neutral-grey-lightest flex w-full flex-wrap items-center gap-2 rounded-lg border px-3 py-2",
                  allowed ? "justify-between" : "justify-center",
                )}>
                <div className="flex items-center gap-2">
                  <NoPackageCircleIcon className="size-6 font-normal" />
                  <span className="text-neutral-black text-sm">{t("common:noActivePlan")}</span>
                </div>
                {allowed && (
                  <Button buttonStyle="Blue" className="h-10 gap-2 px-4 text-sm" onClick={openPlans}>
                    {t("common:joinWithAPlan")}
                    <RightArrowIcon className="h-3 text-white" />
                  </Button>
                )}
              </div>
            )}
          </div>
        )}
      </Can>
      <Can I="update" a="CompanyContentBuyouts" passThrough>
        {allowed => (
          <>
            <div className="flex flex-col gap-1">
              <span className="text-neutral-black text-sm font-medium">{t("common:contentBuyoutsRemaining")}</span>
              <div
                className="border-neutral-grey-light-dark flex flex-col items-center rounded-lg border"
                id="contentBuyouts">
                <div
                  className={clsxm(
                    "flex items-center gap-2 self-stretch px-3 py-2",
                    allowed ? "justify-between" : "justify-center",
                  )}>
                  <div className="flex items-center gap-2">
                    <SourceContentIcon className="text-neutral-black size-3.5" />
                    {remainingBuyouts > INFINITY_THRESHOLD ? (
                      <InfinityIcon className="text-neutral-black h-4 w-5" />
                    ) : (
                      <span className="text-neutral-black text-sm font-semibold">{remainingBuyouts}</span>
                    )}
                  </div>
                  {allowed && !isSubbrand && isViewingSelf && (
                    <button
                      className="group hover:bg-neutral-white-dark rounded-md p-1.5 transition-all duration-150"
                      onClick={() =>
                        setSettingsModal(state => state && {...state, subview: {type: "add_more_buyouts"}})
                      }>
                      <PlusIcon className="text-neutral-black group-hover:text-primary-dark-solid size-4 transition-all duration-150" />
                    </button>
                  )}
                  {allowed && isSubbrand && remainingBuyouts === 0 && (
                    <button
                      className="group hover:bg-neutral-white-dark rounded-md p-1.5 transition-all duration-150"
                      onClick={() =>
                        setSettingsModal(state => state && {...state, subview: {type: "add_more_buyouts"}})
                      }>
                      <MailPlusIcon className="text-primary-dark-solid size-4" />
                    </button>
                  )}
                </div>
                <div className="bg-neutral-grey-light-dark h-px w-full" />
                {!!subscription?.valid_until && (
                  <div className="flex items-center gap-2 px-3 py-2" data-tooltip-id="reset-buyouts">
                    <Tooltip
                      className="border-neutral-white-dark rounded-lg border"
                      classNameArrow="border-neutral-white-dark border-b border-r"
                      id="reset-buyouts"
                      place="bottom"
                      variant="light"
                      delayShow={500}>
                      {t("common:buyoutsWillBeResetted", {buyoutCount: packageIncludedBuyouts})}
                    </Tooltip>
                    <CircularArrowsIcon className="text-neutral-grey-dark size-2.5" />
                    <span className="text-neutral-grey-dark text-2xs">
                      {dayjs(subscription.valid_until).format("DD. MMMM YYYY.")}
                    </span>
                  </div>
                )}
              </div>
            </div>
            {(isAgencyScope || isSubbrand) && (
              <div className="flex flex-col gap-1">
                <label htmlFor="campignsLeft" className="text-neutral-black m-0 text-sm font-medium">
                  {t("multi_account:remaining_campaings")}
                </label>
                <div
                  className="border-neutral-grey-light-dark flex flex-col items-center rounded-lg border"
                  id="campignsLeft">
                  <div
                    className={clsxm(
                      "flex items-center gap-2 self-stretch px-3 py-2",
                      allowed ? "justify-between" : "justify-center",
                    )}>
                    <div className="flex items-center gap-2">
                      <CampaignIcon className="text-neutral-black size-3.5" />
                      {campaignsLeft > INFINITY_THRESHOLD ? (
                        <InfinityIcon className="text-neutral-black h-4 w-5" />
                      ) : (
                        <span className="text-neutral-black text-sm font-semibold">{campaignsLeft}</span>
                      )}
                    </div>
                    {allowed && !isSubbrand && isViewingSelf && (
                      <button
                        className="group hover:bg-neutral-white-dark rounded-md p-1.5 transition-all duration-150"
                        onClick={() =>
                          setSettingsModal(state => state && {...state, subview: {type: "add_more_campaigns"}})
                        }>
                        <PlusIcon className="text-neutral-black group-hover:text-primary-dark-solid size-4 transition-all duration-150" />
                      </button>
                    )}
                    {allowed && isSubbrand && campaignsLeft === 0 && (
                      <button
                        className="group hover:bg-neutral-white-dark rounded-md p-1.5 transition-all duration-150"
                        onClick={() =>
                          setSettingsModal(state => state && {...state, subview: {type: "add_more_campaigns"}})
                        }>
                        <MailPlusIcon className="text-primary-dark-solid size-4 transition-all duration-150" />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </Can>
      <div className="flex flex-col gap-1">
        <span className="text-neutral-black text-sm font-medium">{t("common:unlockedFeatures")}</span>
        <UnlockedFeaturesList
          addOnsListClassName="gap-x-6 gap-y-2"
          className="gap-x-6 gap-y-2 text-sm"
          iconClassName="mr-0 size-4"
          id="unlockedFeatures"
          subscription={subscription}
        />
      </div>
      <LockedFeaturesList
        addOnsListClassName="gap-x-6 gap-y-2"
        className="gap-x-6 gap-y-2 text-sm"
        iconClassName="mr-0 size-4"
        labelClassName="text-neutral-black"
        subscription={subscription}
      />
      <Can I="update" a="CompanyBillingAddress" passThrough>
        {allowed => (
          <div className="flex flex-col gap-2">
            <InputField
              className="border-neutral-grey-light-dark h-11 rounded-lg px-3 text-sm"
              isDisabled={!allowed}
              label={t("common:billingAddress")}
              labelClassName="text-neutral-black text-sm font-medium"
              placeholder={t("common:companiesAddressPlaceholder")}
              value={billingAddress.street}
            />
            <div className="flex flex-row gap-2">
              <InputField
                className="border-neutral-grey-light-dark h-11 rounded-lg px-3 text-sm"
                containerClassName="flex-1"
                isDisabled={!allowed}
                placeholder={t("common:postal_code")}
                value={billingAddress.postal}
              />
              <InputField
                className="border-neutral-grey-light-dark h-11 rounded-lg px-3 text-sm"
                containerClassName="flex-1"
                isDisabled={!allowed}
                placeholder={t("common:city")}
                value={billingAddress.city}
              />
            </div>
            <CountrySelect
              buttonClassName="border-neutral-grey-light-dark h-11 w-full justify-start gap-2 rounded-lg border text-sm"
              chevronClassName="ml-auto mr-2"
              disabled
              hidePhoneNumber
              onChange={() => {}}
              showCountryName
              value={getCountryCodeByEnLabel(billingAddress.country)}
            />
          </div>
        )}
      </Can>
      {(!!paymentMethods?.length || canAddPaymentMethod) && (
        <Can I="list" a="CompanyPaymentMethod">
          <div className="flex flex-col gap-2">
            <span className="text-neutral-black text-sm font-medium">{t("settings:payment_methods")}</span>
            <div className="flex flex-col gap-4">
              {paymentMethods?.map((method, index, array) => (
                <CardComponent
                  agencyId={agencyId}
                  billingDetails={{
                    city: billingAddress.city,
                    country: billingAddress.country,
                    phone: isAgencyScope ? undefined : company.phonenumber,
                    postalCode: billingAddress.postal,
                    street: billingAddress.street,
                  }}
                  card={method.card}
                  isDeletable={array.length > 1}
                  isSelected={index === 0}
                  isSelectLoading={setDefaultLoading}
                  key={method.id}
                  onSelect={() => {
                    if (array.length < 2 || index === 0) return;

                    setDefaultPaymentMethod({paymentMethodId: method.id});
                  }}
                  paymentMethodId={method.id}
                  showButtons={isViewingSelf}
                  type={method.type}
                />
              ))}
            </div>
            {canAddPaymentMethod && (
              <Can I="create" a="CompanyPaymentMethod">
                {isAddingPaymentMethod ? (
                  <AddPaymentMethodForm
                    currency={company?.currency ?? "CHF"}
                    onClose={() => setIsAddingPaymentMethod(false)}
                  />
                ) : (
                  <Button
                    buttonStyle="White"
                    className="h-10 items-center gap-2 self-start px-4 text-sm"
                    onClick={() => {
                      rememberStripeSetupReturn({
                        agencyId,
                        companyId: company.id,
                        settingsModal: {scope, section: "billing"},
                      });
                      setIsAddingPaymentMethod(true);
                    }}>
                    <PlusIcon className="size-4 shrink-0" />
                    {t("common:add_payment_method")}
                  </Button>
                )}
              </Can>
            )}
          </div>
        </Can>
      )}
    </div>
  );
};

export default BillingPane;
